import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, AreaChart, Area, ComposedChart,
  PieChart, Pie, Cell,
  ScatterChart, Scatter,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ReferenceLine
} from 'recharts';
import screenReportData from './screenReportData.json';
import musicReportData from './musicReportData.json';
import gamesReportData from './gamesReportData.json';
import fashionReportData from './fashionReportData.json';
import advertisingReportData from './advertisingReportData.json';
import creatorsReportData from './creatorsReportData.json';
import craftReportData from './craftReportData.json';
import infrastructureReportData from './infrastructureReportData.json';
import educationReportData from './educationReportData.json';
import performingReportData from './performingReportData.json';
import aiReportData from './aiReportData.json';
import socialReportData from './socialReportData.json';
import { REPORT_CHARTS_BY_SLUG } from './reportCharts';

const REPORT_DATA_BY_SLUG = {
  screen: screenReportData,
  music: musicReportData,
  games: gamesReportData,
  fashion: fashionReportData,
  advertising: advertisingReportData,
  creators: creatorsReportData,
  craft: craftReportData,
  infrastructure: infrastructureReportData,
  education: educationReportData,
  performing: performingReportData,
  ai: aiReportData,
  social: socialReportData
};

const COLORS = {
  red: '#ff6b6b',
  teal: '#4ecdc4',
  blue: '#45b7d1',
  green: '#96ceb4',
  yellow: '#ffeaa7',
  purple: '#a29bfe',
  grey: '#636e72'
};

const CONTACT_URL = 'https://www.obsolete.com/about-us/contact/';

const publicBaseUrl = process.env.PUBLIC_URL ? process.env.PUBLIC_URL.replace(/\/$/, '') : '';
const resolveReportAssetUrl = (src) => {
  if (!src || typeof src !== 'string') return src;
  if (!publicBaseUrl) return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith(publicBaseUrl + '/')) return src;
  if (src.startsWith('/')) return `${publicBaseUrl}${src}`;
  return src;
};

const ReportImage = ({ src, alt, className, style }) => {
  const [didError, setDidError] = useState(false);
  const [didLoad, setDidLoad] = useState(false);
  const resolvedSrc = resolveReportAssetUrl(src);

  if (!resolvedSrc || didError) {
    return (
      <div
        className={className}
        style={{
          ...(style || {}),
          background: '#dfe6e9',
          border: '2px dashed #636e72',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#636e72',
          fontSize: '10pt'
        }}
      >
        {alt || 'Image unavailable'}
      </div>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt || ''}
      className={className}
      style={{ ...(style || {}), display: didLoad ? 'block' : 'block' }}
      onLoad={() => setDidLoad(true)}
      onError={() => setDidError(true)}
    />
  );
};

const CalloutBox = ({ color, title, children, style }) => (
  <div className="callout-box" style={{
    background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
    border: `2px solid ${color}`,
    borderRadius: '8px',
    padding: '6mm',
    margin: '0 0 8mm',
    borderRadius: '3px',
    ...style
  }}>
    <h3 style={{ 
      color: color, 
      fontSize: '11pt', 
      fontWeight: '700', 
      marginTop: 0, 
      marginBottom: '5px' 
    }}>{title}</h3>
    <div style={{ fontSize: '9pt', lineHeight: '1.5' }}>{children}</div>
  </div>
);

const renderParagraphContent = (content) => {
  if (!content) return null;
  if (Array.isArray(content)) {
    return content
      .filter(Boolean)
      .map((para, idx) => (
        <p key={idx} style={{ marginTop: idx === 0 ? 0 : '6px' }}>
          {para}
        </p>
      ));
  }
  if (typeof content === 'string') {
    return <p style={{ marginTop: 0 }}>{content}</p>;
  }
  return <p style={{ marginTop: 0 }}>{String(content)}</p>;
};

const MarkdownField = ({ content }) => {
  if (!content) return null;
  
  // Handle both string and array content
  const contentStr = Array.isArray(content) ? content.join('\n\n') : content;
  
  // Split by double newlines for paragraphs, then single newlines within paragraphs
  const paragraphs = contentStr.split(/\n\n+/);

  return (
    <>
      {paragraphs.map((para, paraIdx) => {
        const lines = para.split('\n');

        return (
          <React.Fragment key={paraIdx}>
            <span>
              {lines.map((line, lineIdx) => {
                const boldParts = line.split(/\*\*([^*]+)\*\*/);
                return (
                  <React.Fragment key={lineIdx}>
                    {boldParts.map((part, partIdx) =>
                      partIdx % 2 === 0 ? part : <strong key={partIdx}>{part}</strong>
                    )}
                    {lineIdx < lines.length - 1 && <br />}
                  </React.Fragment>
                );
              })}
            </span>
            {paraIdx < paragraphs.length - 1 && (
              <>
                <br />
                <br />
              </>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const combinedExplanationLooksLikeActionDetails = (content) => {
  if (!content) return false;
  const contentStr = Array.isArray(content) ? content.join('\n\n') : String(content);
  return /\*\*\s*(agent|brief|impact|odds|rationale|failure\s*mode|friction|implementation)\s*:\s*\*\*/i.test(contentStr)
    || /^\s*#{2,4}\s*action\s*\d+/im.test(contentStr)
    || /\*\*\s*action\s*\d+\s*:/i.test(contentStr)
    || /^\s*(agent|brief|impact|odds|rationale|failure\s*mode|friction|implementation)\s*:/im.test(contentStr);
};

const CausalFlow = ({ chain }) => {
  if (!chain || chain.length === 0) return null;
  
  // Horizontal single-row layout
  const nodeWidth = 160;
  const nodeHeight = 65;
  const horizontalGap = 35;
  const padding = 20;
  
  const svgWidth = (nodeWidth * chain.length) + (horizontalGap * (chain.length - 1)) + (padding * 2);
  const svgHeight = nodeHeight + (padding * 2);
  
  return (
    <div className="causal-flow" style={{
      margin: '12px 0',
      overflowX: 'auto'
    }}>
      <svg
        className="causal-flow-svg"
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMinYMin meet"
        style={{
          background: '#ffffff',
          border: '1px solid #dee2e6',
          borderRadius: '6px'
        }}
      >
        {chain.map((item, i) => {
          const x = padding + (i * (nodeWidth + horizontalGap));
          const y = padding;
          
          const hasNext = i < chain.length - 1;
          const nextX = padding + ((i + 1) * (nodeWidth + horizontalGap));
          
          return (
            <g key={i}>
              {/* Node box */}
              <rect
                x={x}
                y={y}
                width={nodeWidth}
                height={nodeHeight}
                fill="#f8f9fa"
                stroke="#495057"
                strokeWidth="2"
                rx="6"
              />
              
              {/* Node text */}
              <foreignObject
                x={x + 10}
                y={y + 10}
                width={nodeWidth - 20}
                height={nodeHeight - 20}
              >
                <div style={{
                  fontSize: '8.5pt',
                  lineHeight: '1.35',
                  color: '#212529',
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  fontWeight: '500'
                }}>
                  {item}
                </div>
              </foreignObject>
              
              {/* Arrow to next node */}
              {hasNext && (
                <g>
                  <line
                    x1={x + nodeWidth}
                    y1={y + nodeHeight / 2}
                    x2={nextX}
                    y2={y + nodeHeight / 2}
                    stroke="#495057"
                    strokeWidth="2.5"
                    markerEnd="url(#arrowhead)"
                  />
                </g>
              )}
            </g>
          );
        })}
        
        {/* Arrow marker definition */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3, 0 6"
              fill="#495057"
            />
          </marker>
        </defs>
      </svg>
    </div>
  );
};

const LogicBox = ({ logic, nodeLabels }) => {
  if (!logic || logic.length === 0) return null;
  
  const causalChain = [];
  const metaInfo = {};
  
  logic.forEach(item => {
    if (typeof item === 'string') {
      const nodeMatch = item.match(/^NODE:\s*([a-z0-9_]+)/i);
      const shiftMatch = item.match(/^SHIFT:\s*(.+)$/i);
      
      if (nodeMatch) {
        const nodeId = nodeMatch[1];
        const label = nodeLabels?.[nodeId];
        metaInfo.node = { id: nodeId, label };
      } else if (shiftMatch) {
        metaInfo.shift = shiftMatch[1];
      } else {
        // Split on arrow characters to create separate chain steps
        const steps = item.split(/\s*→\s*/).filter(s => s.trim());
        if (steps.length > 1) {
          causalChain.push(...steps);
        } else {
          causalChain.push(item);
        }
      }
    }
  });
  
  return (
    <div className="logic-box break-avoid" style={{
      background: '#ffffff',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '16px',
      marginBottom: '16px'
    }}>
      {(metaInfo.node || metaInfo.shift) && (
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '14px',
          paddingBottom: '12px',
          borderBottom: '1px solid #e9ecef',
          fontSize: '8.5pt'
        }}>
          {metaInfo.node && (
            <div>
              <span style={{ fontWeight: '600', color: '#495057' }}>NODE: </span>
              <span style={{ color: '#6c757d' }}>
                {metaInfo.node.id}
                {metaInfo.node.label && ` — ${metaInfo.node.label}`}
              </span>
            </div>
          )}
          {metaInfo.shift && (
            <div>
              <span style={{ fontWeight: '600', color: '#495057' }}>SHIFT: </span>
              <span style={{ color: '#6c757d' }}>{metaInfo.shift}</span>
            </div>
          )}
        </div>
      )}
      
      {causalChain.length > 0 && (
        <div>
          <div style={{
            fontSize: '9pt',
            fontWeight: '600',
            color: '#495057',
            marginBottom: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Causal Chain
          </div>
          <CausalFlow chain={causalChain} />
        </div>
      )}
    </div>
  );
};

const ActionBlock = ({ title, agent, brief, mechanism, impact, odds, friction, rationale, failureMode }) => (
    <div className="action-block" style={{
      background: '#ffffff',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <h4 style={{
        margin: '0 0 12px 0',
        fontSize: '10pt',
        fontWeight: '600',
        color: '#212529',
        paddingBottom: '8px',
        borderBottom: '2px solid #e9ecef'
      }}>{title}</h4>
      
      <div style={{ fontSize: '8.5pt', lineHeight: '1.5' }}>
        {agent && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontWeight: '600', color: '#495057' }}>Agent: </span>
            <span style={{ color: '#6c757d' }}><MarkdownField content={agent} /></span>
          </div>
        )}
        
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontWeight: '600', color: '#495057' }}>Brief: </span>
          <span style={{ color: '#6c757d' }}><MarkdownField content={brief} /></span>
        </div>
        
        {mechanism && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontWeight: '600', color: '#495057' }}>Implementation: </span>
            <span style={{ color: '#6c757d' }}><MarkdownField content={mechanism} /></span>
          </div>
        )}
        
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontWeight: '600', color: '#495057' }}>Impact: </span>
          <span style={{ color: '#6c757d' }}><MarkdownField content={impact} /></span>
        </div>
        
        {friction && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontWeight: '600', color: '#495057' }}>Friction: </span>
            <span style={{ color: '#6c757d' }}><MarkdownField content={friction} /></span>
          </div>
        )}
        
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontWeight: '600', color: '#495057' }}>Odds: </span>
          <span style={{ color: '#28a745', fontWeight: '600' }}><MarkdownField content={odds} /></span>
        </div>
        
        {rationale && (
          <div style={{ 
            marginTop: '12px', 
            paddingTop: '12px', 
            borderTop: '1px solid #e9ecef'
          }}>
            <div style={{ fontWeight: '600', color: '#495057', marginBottom: '6px' }}>Rationale:</div>
            <div style={{ color: '#6c757d', fontSize: '8pt', lineHeight: '1.5' }}>
              <MarkdownField content={rationale} />
            </div>
          </div>
        )}
        
        {failureMode && (
          <div style={{ 
            marginTop: '12px', 
            paddingTop: '12px', 
            borderTop: '1px solid #e9ecef'
          }}>
            <span style={{ fontWeight: '600', color: '#dc3545' }}>Failure Mode: </span>
            <span style={{ color: '#6c757d' }}><MarkdownField content={failureMode} /></span>
          </div>
        )}
      </div>
    </div>
);

const waitForImagesToLoad = async () => {
  if (typeof document === 'undefined') return;
  const images = Array.from(document.images || []);
  await Promise.all(images.map(async (img) => {
    if (!img || img.complete) {
      if (typeof img?.decode === 'function') {
        try { await img.decode(); } catch {}
      }
      return;
    }
    await new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
    if (typeof img?.decode === 'function') {
      try { await img.decode(); } catch {}
    }
  }));
};

const PerformingReportTemplate = ({ reportSlug = 'performing' }) => {
  const [pagedReady, setPagedReady] = useState(false);
  const didInitPagedRef = useRef(false);

  const reportData = REPORT_DATA_BY_SLUG?.[reportSlug] || performingReportData;
  const reportCharts = {
    ...(REPORT_CHARTS_BY_SLUG?.[reportSlug] || {}),
    ...(reportData?.charts && typeof reportData.charts === 'object' ? reportData.charts : {})
  };

  const defaultInlineChartIds = Object.keys(reportCharts);
  const getFallbackPredictionCharts = (predictionIndex) => {
    const totalPredictions = Array.isArray(reportData?.narrative?.predictions)
      ? reportData.narrative.predictions.length
      : 0;
    const totalCharts = defaultInlineChartIds.length;

    if (totalPredictions <= 0 || totalCharts === 0) return [];

    const chartsPerPrediction = Math.ceil(totalCharts / totalPredictions);
    const start = predictionIndex * chartsPerPrediction;
    const ids = defaultInlineChartIds.slice(start, start + chartsPerPrediction);
    return ids.map((id) => ({ id }));
  };

  const isPagedMode = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('paged') === '1';
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    console.debug('[PerformingReportTemplate]', { reportSlug, chartCount: Object.keys(reportCharts).length });
    const runPaged = async () => {
      if (!isPagedMode) return;
      if (didInitPagedRef.current) return;
      didInitPagedRef.current = true;

      try {
        setPagedReady(false);
        await waitForImagesToLoad();
        if (typeof document !== 'undefined' && document.fonts?.ready) {
          try { await document.fonts.ready; } catch {}
        }
        setPagedReady(true);
      } catch (err) {
        console.error('[PerformingReportTemplate] Print-friendly mode init failed', err);
        setPagedReady(true);
      }
    };
    runPaged();
  }, [isPagedMode]);

  const renderRichLine = (text) => {
    if (typeof text !== 'string') return null;
    const m = text.match(/^\*\*(.+?)\*\*:\s*(.*)$/);
    if (m) {
      return (
        <p>
          <strong>{m[1]}:</strong>{' '}{m[2]}
        </p>
      );
    }
    return <p>{text}</p>;
  };

  const renderLogicLine = (text) => {
    if (typeof text !== 'string') return null;
    const nodeMatch = text.match(/^NODE:\s*([a-z0-9_]+)/i);
    if (nodeMatch) {
      const nodeId = nodeMatch[1];
      const label = reportData.nodeLabels?.[nodeId];
      return (
        <p><strong>NODE:</strong> {nodeId}{label ? ` — ${label}` : ''}</p>
      );
    }
    const shiftMatch = text.match(/^SHIFT:\s*(.+)$/i);
    if (shiftMatch) {
      return (
        <p><strong>SHIFT:</strong> {shiftMatch[1]}</p>
      );
    }
    return <p><strong>{text}</strong></p>;
  };

  const renderChartById = (chartId, chartDef) => {
    if (!chartDef?.data) return null;

    if (chartId === 'touringCosts') {
      return (
        <ComposedChart data={chartDef.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="year" tick={{ fontSize: 8 }} />
          <YAxis yAxisId="left" tick={{ fontSize: 8 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 8 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Area yAxisId="left" type="monotone" dataKey="tourCostHigh" fill={COLORS.red} stroke={COLORS.red} fillOpacity={0.2} name="Tour cost (high)" />
          <Area yAxisId="left" type="monotone" dataKey="tourCostLow" fill={COLORS.teal} stroke={COLORS.teal} fillOpacity={0.18} name="Tour cost (low)" />
          <Line yAxisId="right" type="monotone" dataKey="diesel" stroke={COLORS.grey} strokeWidth={2} name="Diesel £/litre" />
        </ComposedChart>
      );
    }

    if (chartId === 'insurancePremium' || chartId === 'warehouseMigration') {
      return (
        <AreaChart data={chartDef.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="year" tick={{ fontSize: 8 }} />
          <YAxis tick={{ fontSize: 8 }} />
          <Tooltip formatter={(v) => [`${v}%`, '']} />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Area type="monotone" dataKey="premiumPctHigh" stroke={COLORS.red} fill={COLORS.red} fillOpacity={0.25} name="Premium % (high)" />
          <Area type="monotone" dataKey="premiumPctLow" stroke={COLORS.teal} fill={COLORS.teal} fillOpacity={0.25} name="Premium % (low)" />
        </AreaChart>
      );
    }

    if (chartId === 'hologramVsLive') {
      return (
        <ComposedChart data={chartDef.data} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis type="number" tick={{ fontSize: 8 }} />
          <YAxis type="category" dataKey="category" tick={{ fontSize: 8 }} width={160} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Bar dataKey="cost" fill={COLORS.teal} name="Cost (£K)" />
          <Line type="monotone" dataKey="risk" stroke={COLORS.red} strokeWidth={2} name="Cancellation risk %" />
        </ComposedChart>
      );
    }

    if (chartId === 'payToPlayVenues') {
      return (
        <LineChart data={chartDef.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="year" tick={{ fontSize: 8 }} />
          <YAxis tick={{ fontSize: 8 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Line type="monotone" dataKey="venues" stroke={COLORS.red} strokeWidth={2} name="Venues" />
        </LineChart>
      );
    }

    if (chartId === 'phoneFreePremium') {
      return (
        <BarChart data={chartDef.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="model" tick={{ fontSize: 8 }} />
          <YAxis tick={{ fontSize: 8 }} />
          <Tooltip formatter={(v) => [`${v}%`, 'Uplift']} />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Bar dataKey="upliftPct" fill={COLORS.blue} name="Ticket uplift" />
        </BarChart>
      );
    }

    return null;
  };

  const renderGenericChart = (chartId, chartDef) => {
    if (!chartDef?.data || !Array.isArray(chartDef.data) || chartDef.data.length === 0) return null;

    const type = chartDef.type;
    const config = chartDef.config || {};
    const palette = [COLORS.red, COLORS.teal, COLORS.blue, COLORS.green, COLORS.yellow, COLORS.purple];

    const computeYAxisIds = (series) => {
      const ids = new Set();
      (series || []).forEach((s) => {
        if (s && typeof s.yAxisId === 'string' && s.yAxisId.trim()) ids.add(s.yAxisId);
      });
      if (ids.size === 0) ids.add('left');
      // Keep stable ordering for common two-axis charts.
      const ordered = Array.from(ids);
      if (ordered.includes('left') && ordered.includes('right')) {
        return ['left', 'right', ...ordered.filter((x) => x !== 'left' && x !== 'right')];
      }
      return ordered;
    };

    const renderYAxes = (yAxisIds) => {
      if (!Array.isArray(yAxisIds) || yAxisIds.length === 0) {
        return <YAxis tick={{ fontSize: 8 }} />;
      }
      if (yAxisIds.length === 1) {
        return <YAxis yAxisId={yAxisIds[0]} tick={{ fontSize: 8 }} />;
      }
      return (
        <>
          <YAxis yAxisId={yAxisIds[0]} tick={{ fontSize: 8 }} />
          <YAxis yAxisId={yAxisIds[1]} orientation="right" tick={{ fontSize: 8 }} />
        </>
      );
    };

    if (type === 'bar') {
      const layout = config.layout === 'vertical' ? 'vertical' : 'horizontal';
      const xKey = config.xKey || Object.keys(chartDef.data[0] || {})[0];
      const bars = Array.isArray(config.bars) && config.bars.length > 0
        ? config.bars
        : [{ dataKey: Object.keys(chartDef.data[0] || {})[1], name: 'Value' }];

      const yAxisIds = computeYAxisIds(bars);
      const isVertical = layout === 'vertical';

      return (
        <BarChart data={chartDef.data} layout={layout === 'vertical' ? 'vertical' : undefined}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          {layout === 'vertical' ? (
            <>
              <XAxis type={config.xAxisType || 'number'} tick={{ fontSize: 8 }} />
              <YAxis type="category" dataKey={xKey} width={140} tick={{ fontSize: 8 }} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} tick={{ fontSize: 8 }} angle={-15} textAnchor="end" height={60} />
              {renderYAxes(yAxisIds)}
            </>
          )}
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          {Array.isArray(config.referenceLines) && config.referenceLines.map((ref, i) => (
            <ReferenceLine
              key={`${chartId}-ref-${i}`}
              x={ref.x}
              y={ref.y}
              stroke={ref.stroke || COLORS.grey}
              strokeDasharray={ref.strokeDasharray}
              label={ref.label}
            />
          ))}
          {bars.map((b, i) => (
            <Bar
              key={`${chartId}-bar-${b.dataKey || i}`}
              dataKey={b.dataKey}
              name={b.name}
              fill={b.fill || palette[i % palette.length]}
              stackId={b.stackId}
              yAxisId={isVertical ? undefined : (b.yAxisId || yAxisIds[0])}
            />
          ))}
        </BarChart>
      );
    }

    if (type === 'line') {
      const xKey = config.xKey || Object.keys(chartDef.data[0] || {})[0];
      const lines = Array.isArray(config.lines) && config.lines.length > 0
        ? config.lines
        : [{ dataKey: Object.keys(chartDef.data[0] || {})[1], name: 'Value' }];

      const yAxisIds = computeYAxisIds(lines);

      return (
        <LineChart data={chartDef.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 8 }} />
          {renderYAxes(yAxisIds)}
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          {Array.isArray(config.referenceLines) && config.referenceLines.map((ref, i) => (
            <ReferenceLine
              key={`${chartId}-ref-${i}`}
              x={ref.x}
              y={ref.y}
              stroke={ref.stroke || COLORS.grey}
              strokeDasharray={ref.strokeDasharray}
              label={ref.label}
            />
          ))}
          {lines.map((l, i) => (
            <Line
              key={`${chartId}-line-${l.dataKey || i}`}
              type="monotone"
              dataKey={l.dataKey}
              name={l.name}
              stroke={l.stroke || palette[i % palette.length]}
              strokeWidth={l.strokeWidth || 2}
              dot={false}
              yAxisId={l.yAxisId || yAxisIds[0]}
            />
          ))}
        </LineChart>
      );
    }

    if (type === 'area') {
      const xKey = config.xKey || Object.keys(chartDef.data[0] || {})[0];
      const areas = Array.isArray(config.areas) && config.areas.length > 0
        ? config.areas
        : Object.keys(chartDef.data[0] || {})
            .filter((k) => k !== xKey)
            .slice(0, 2)
            .map((k) => ({ dataKey: k, name: k }));

      return (
        <AreaChart data={chartDef.data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 8 }} />
          <YAxis tick={{ fontSize: 8 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          {Array.isArray(config.referenceLines) && config.referenceLines.map((ref, i) => (
            <ReferenceLine
              key={`${chartId}-ref-${i}`}
              x={ref.x}
              y={ref.y}
              stroke={ref.stroke || COLORS.grey}
              strokeDasharray={ref.strokeDasharray}
              label={ref.label}
            />
          ))}
          {areas.map((a, i) => (
            <Area
              key={`${chartId}-area-${a.dataKey || i}`}
              type="monotone"
              dataKey={a.dataKey}
              name={a.name}
              stroke={a.stroke || palette[i % palette.length]}
              fill={a.fill || palette[i % palette.length]}
              fillOpacity={0.22}
            />
          ))}
        </AreaChart>
      );
    }

    if (type === 'pie') {
      const nameKey = config.nameKey || Object.keys(chartDef.data[0] || {})[0];
      const dataKey = config.dataKey || Object.keys(chartDef.data[0] || {})[1];

      return (
        <PieChart>
          <Pie
            data={chartDef.data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            outerRadius={80}
            label
          >
            {chartDef.data.map((_, index) => (
              <Cell key={`${chartId}-cell-${index}`} fill={palette[index % palette.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
        </PieChart>
      );
    }

    if (type === 'composed') {
      const xKey = config.xKey || Object.keys(chartDef.data[0] || {})[0];
      const bars = Array.isArray(config.bars) ? config.bars : [];
      const lines = Array.isArray(config.lines) ? config.lines : [];
      const areas = Array.isArray(config.areas) ? config.areas : [];

      const yAxisIds = computeYAxisIds([...bars, ...lines, ...areas]);
      const isVertical = config.layout === 'vertical';

      return (
        <ComposedChart data={chartDef.data} layout={config.layout === 'vertical' ? 'vertical' : undefined}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          {config.layout === 'vertical' ? (
            <>
              <XAxis type={config.xAxisType || 'number'} tick={{ fontSize: 8 }} />
              <YAxis type="category" dataKey={xKey} width={140} tick={{ fontSize: 8 }} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} tick={{ fontSize: 8 }} angle={-15} textAnchor="end" height={60} />
              {renderYAxes(yAxisIds)}
            </>
          )}
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          {Array.isArray(config.referenceLines) && config.referenceLines.map((ref, i) => (
            <ReferenceLine
              key={`${chartId}-ref-${i}`}
              x={ref.x}
              y={ref.y}
              stroke={ref.stroke || COLORS.grey}
              strokeDasharray={ref.strokeDasharray}
              label={ref.label}
            />
          ))}
          {bars.map((b, i) => (
            <Bar
              key={`${chartId}-bar-${b.dataKey || i}`}
              dataKey={b.dataKey}
              name={b.name}
              fill={b.fill || palette[i % palette.length]}
              stackId={b.stackId}
              yAxisId={isVertical ? undefined : (b.yAxisId || yAxisIds[0])}
            />
          ))}
          {areas.map((a, i) => (
            <Area
              key={`${chartId}-area-${a.dataKey || i}`}
              type="monotone"
              dataKey={a.dataKey}
              name={a.name}
              fill={a.fill || palette[i % palette.length]}
              stroke={a.stroke || palette[i % palette.length]}
              fillOpacity={a.fillOpacity ?? 0.22}
              yAxisId={isVertical ? undefined : (a.yAxisId || yAxisIds[0])}
            />
          ))}
          {lines.map((l, i) => (
            <Line
              key={`${chartId}-line-${l.dataKey || i}`}
              type={l.type || 'monotone'}
              dataKey={l.dataKey}
              name={l.name}
              stroke={l.stroke || palette[i % palette.length]}
              strokeWidth={l.strokeWidth || 2}
              dot={l.dot ?? false}
              yAxisId={isVertical ? undefined : (l.yAxisId || yAxisIds[0])}
            />
          ))}
        </ComposedChart>
      );
    }

    if (type === 'scatter') {
      const xKey = config.xKey || Object.keys(chartDef.data[0] || {})[0];
      const yKey = config.yKey || Object.keys(chartDef.data[0] || {})[1];

      return (
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey={xKey} type={config.xAxisType || 'number'} tick={{ fontSize: 8 }} />
          <YAxis dataKey={yKey} tick={{ fontSize: 8 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Scatter name={config.name || 'Series'} data={chartDef.data} fill={config.fill || COLORS.teal} />
        </ScatterChart>
      );
    }

    if (type === 'radar') {
      const angleKey = config.angleKey || Object.keys(chartDef.data[0] || {})[0];
      const dataKey = config.dataKey || Object.keys(chartDef.data[0] || {})[1];

      return (
        <RadarChart data={chartDef.data} outerRadius={80}>
          <PolarGrid />
          <PolarAngleAxis dataKey={angleKey} tick={{ fontSize: 8 }} />
          <PolarRadiusAxis tick={{ fontSize: 8 }} />
          <Radar
            dataKey={dataKey}
            stroke={config.stroke || COLORS.teal}
            fill={config.fill || COLORS.teal}
            fillOpacity={config.fillOpacity ?? 0.35}
            name={config.name || dataKey}
          />
          <Legend wrapperStyle={{ fontSize: '9pt' }} />
          <Tooltip />
        </RadarChart>
      );
    }

    return null;
  };

  return (
    <div className={isPagedMode ? 'report-container paged-mode' : 'report-container'}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;600;700;900&display=swap');

        @page { size: A4 portrait; margin: 0; }

        :root {
          --obsolete-yellow: #ffff66;
          --obsolete-black: #000000;
          --obsolete-white: #ffffff;
          --obsolete-text: #111111;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .report-container {
          width: 100%;
          max-width: min(100%, 1024px);
          margin: 0 auto;
          background: white;
          color: var(--obsolete-text);
          font-family: 'Libre Franklin', sans-serif;
        }

        .report-container.paged-mode { max-width: none; margin: 0; background: transparent; }

        .content-page {
          padding: 15mm 20mm;
          min-height: 210mm;
          background: white;
        }

        /* Prevent components from splitting across pages */
        .avoid-break,
        .action-block,
        .callout-box,
        .logic-box {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .title-page {
          display: block;
          height: 210mm;
          position: relative;
          background: #ffffff;
          padding: 0;
          overflow: hidden;
          page-break-inside: avoid;
          break-inside: avoid;
          page-break-after: always;
          break-after: page;
        }

        .cover-hero { position: absolute; inset: 0; }
        .cover-image { width: 100%; height: 100%; object-fit: cover; display: block; }
        .cover-shade {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.0) 45%, rgba(255,255,255,0.7) 70%, rgba(255,255,255,1) 100%);
        }

        .cover-lockup {
          position: absolute;
          left: 50%;
          top: 62%;
          transform: translate(-50%, -50%);
          width: 88%;
          text-align: center;
        }

        .cover-kicker {
          font-size: 12pt;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 700;
          margin-bottom: 6mm;
        }

        .cover-title {
          font-size: 44pt;
          font-weight: 900;
          letter-spacing: -1px;
          line-height: 1.05;
        }

        .cover-subtitle {
          margin-top: 6mm;
          font-size: 14pt;
          font-weight: 600;
          max-width: 240mm;
          margin-left: auto;
          margin-right: auto;
        }

        .toc-page { padding: 15mm 20mm; background: white; }
        .toc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px; }
        .toc-list { list-style: none; padding: 0; margin: 0; }
        .toc-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #dfe6e9;
          font-size: 10pt;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          margin: 12px 0;
        }

        .stat-box {
          border: 2px solid;
          padding: 10px;
          text-align: center;
          background: #f9f9f9;
          border-radius: 3px;
        }

        .stat-number {
          font-size: 18pt;
          font-weight: 900;
          line-height: 1;
        }

        .stat-label {
          font-size: 7.5pt;
          color: #636e72;
          margin-top: 3px;
          font-weight: 600;
        }

        .chart-container {
          margin: 12px 0;
          padding: 10px;
          background: #f9f9f9;
          border-radius: 3px;
          page-break-inside: avoid;
        }

        .chart-title {
          font-size: 10pt;
          font-weight: 700;
          margin-bottom: 3px;
          color: #2c3e50;
        }

        .chart-subtitle {
          font-size: 8pt;
          color: #636e72;
          margin-bottom: 6px;
          font-style: italic;
        }

        .prediction-hero {
          position: relative;
          height: 90mm;
          margin: 0 0 10mm 0;
          overflow: hidden;
          border: 2px solid #000000;
          background: #ffffff;
        }

        .prediction-hero-image { width: 100%; height: 100%; object-fit: cover; display: block; }

        .prediction-hero-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: flex-end;
          padding: 10mm;
          background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 55%, rgba(255,255,255,0.85) 80%, rgba(255,255,255,1) 100%);
        }

        .prediction-hero-title { font-size: 26pt; font-weight: 900; line-height: 1.1; margin: 0; }
        .prediction-hero-subtitle { margin: 3mm 0 0 0; font-size: 12pt; font-weight: 700; }

        .kicker {
          margin: 10mm 0 4mm;
          font-size: 10pt;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #111111;
        }

        .section-title {
          font-size: 16pt;
          font-weight: 700;
          margin-top: 12px;
          margin-bottom: 8px;
          color: #2c3e50;
          border-bottom: 2px solid #dfe6e9;
          padding-bottom: 4px;
          letter-spacing: normal;
          text-transform: none;
        }

        .dystopian-section,
        .mitigated-section {
          background: #f8f9fa;
          border: 2px solid #000000;
          padding: 6mm;
          margin: 0 0 8mm;
        }

        .report-container.paged-mode .dystopian-section,
        .report-container.paged-mode .mitigated-section,
        .report-container.paged-mode .signal-validation {
          background: transparent;
          border: none;
          border-left: none;
          padding: 0;
        }

        /* Technical blocks: keep structure, remove "dashboard" panels */
        .signal-validation,
        .meta-block,
        .action-block {
          background: transparent;
          border: none;
          border-left: 3px solid #dfe6e9;
          padding: 0 0 0 10px;
          margin: 0 0 8mm;
        }

        .action-block {
          border-left-color: #cbd5e1;
        }

        h1 { font-size: 32pt; font-weight: 900; margin-bottom: 15px; color: #2c3e50; line-height: 1.1; }
        h2 { font-size: 16pt; font-weight: 700; margin-top: 12px; margin-bottom: 8px; color: #2c3e50; border-bottom: 2px solid #dfe6e9; padding-bottom: 4px; }
        h3 { font-size: 11pt; font-weight: 700; margin-top: 8px; margin-bottom: 5px; color: #2c3e50; }
        p { font-size: 9.5pt; line-height: 1.5; margin-bottom: 7px; color: #2c3e50; }

        .paged-mode h1 { font-size: 28pt; }
        .paged-mode h2 { font-size: 14pt; }
        .paged-mode h3 { font-size: 10pt; }
        .paged-mode p { font-size: 9pt; line-height: 1.45; }

        .paged-mode .section-title { font-size: 14pt; }

        .print-button {
          position: fixed;
          bottom: 20px;
          right: 20px;
          padding: 15px 30px;
          background: #ffeaa7;
          color: #2c3e50;
          border: 2px solid #2c3e50;
          border-radius: 5px;
          font-size: 14pt;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          z-index: 1000;
        }

        .paged-secondary-button {
          position: fixed;
          bottom: 20px;
          right: 180px;
          padding: 12px 18px;
          font-size: 14px;
          font-weight: 600;
          background: #2c3e50;
          color: #ffeaa7;
          border: 2px solid #2c3e50;
          border-radius: 6px;
          cursor: pointer;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          z-index: 1000;
        }

        .break-before { page-break-before: always; break-before: page; }
        .break-after { page-break-after: always; break-after: page; }
        .avoid-break { page-break-inside: avoid; break-inside: avoid; }

        @media print {
          .no-print { display: none !important; }
          html, body { background: #ffffff !important; color: #111111 !important; }
          .report-container { background: #ffffff !important; color: #111111 !important; }
          * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }

          /* Typography + pagination helpers */
          p { orphans: 3; widows: 3; }
          .content-page { min-height: 0 !important; }
          h1, h2, h3, .kicker { page-break-after: avoid; break-after: avoid; }
          h1, h2, h3, .kicker { page-break-inside: avoid; break-inside: avoid; }
          h2 + p, h3 + p, .kicker + p { page-break-before: avoid; break-before: avoid; }

          /* When a heading starts a new page, give it breathing room at the top */
          .break-before {
            padding-top: 12mm;
            margin-top: 0 !important;
          }

          /* Prevent logic/causal chain from overflowing page width */
          .causal-flow { overflow-x: visible !important; }
          .causal-flow-svg { width: 100% !important; height: auto !important; display: block !important; }

          /* Make hero images reliably printable (avoid fixed-height + object-fit issues in print/PDF) */
          .prediction-hero {
            height: auto !important;
            overflow: visible !important;
            border: 2px solid #000000;
          }

          .prediction-hero-image {
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
            object-fit: contain !important;
          }

          .prediction-hero-overlay {
            position: static !important;
            inset: auto !important;
            background: none !important;
            padding: 6mm 0 0 0 !important;
          }

          .prediction-hero-title { font-size: 20pt !important; }
          .prediction-hero-subtitle { font-size: 10pt !important; }
        }
      `}</style>

      {/* TITLE PAGE */}
      <div className="title-page">
        <div className="cover-hero">
          <ReportImage src={reportData.images.title} alt={reportData.metadata.title} className="cover-image" />
          <div className="cover-shade" />
        </div>
        <div className="cover-lockup">
          <div className="cover-kicker">2029 Predictions Report</div>
          <div className="cover-title">{reportData.metadata.title}</div>
          <div className="cover-subtitle">The report gives you the full breakdown of all 5 scenarios, the evidence behind them, and the countermeasures that change outcomes.</div>
        </div>
      </div>

      {/* CHAPTER INTRO PAGE */}
      <div className="content-page">
        <h1 style={{ marginTop: '60px' }}>{reportData.metadata.chapter}</h1>
        <h2 style={{ fontSize: '14pt', borderBottom: 'none', marginBottom: '15px' }}>What Could Work in 2029</h2>

        <h3 className="kicker">Sector Introduction</h3>
        <div className="dystopian-section">
          {reportData.narrative.sectorIntro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <CalloutBox color={COLORS.teal} title="INTERVENTION POSSIBLE">
          The live performance sector faces five simultaneous pressure points but <strong>none of this is inevitable.</strong> Each prediction includes documented countermeasures with 58-73% success probability, 12-36 month intervention windows.
        </CalloutBox>

        <h2 className="section-title break-before" style={{ marginTop: '15px' }}>The Numbers That Matter</h2>

        {reportData.narrative.numbers?.headline && (
          <p><strong>{reportData.narrative.numbers.headline}</strong></p>
        )}

        {Array.isArray(reportData.narrative.numbers?.paragraphs) && reportData.narrative.numbers.paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}

        {Array.isArray(reportData.narrative.numbers?.bullets) && reportData.narrative.numbers.bullets.length > 0 && (
          <div className="signal-validation" style={{ marginTop: '10px' }}>
            {reportData.narrative.numbers.bullets.map((b, i) => (
              <p key={i}><strong>•</strong> {b}</p>
            ))}
          </div>
        )}

        {Array.isArray(reportData?.stats) && reportData.stats.length > 0 && (
          <div className="stats-grid">
            {reportData.stats.map((stat, idx) => (
              <div key={idx} className="stat-box" style={{ borderColor: stat.color }}>
                <div className="stat-number" style={{ color: stat.color }}>{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PREDICTIONS */}
      {(reportData?.narrative?.predictions || []).map((pred, idx) => (
        <div key={idx} className="content-page break-before">
          <div className="prediction-hero">
            {reportData.images.predictions[idx] && (
              <ReportImage
                src={reportData.images.predictions[idx]}
                alt={pred.title}
                className="prediction-hero-image"
              />
            )}
            <div className="prediction-hero-overlay">
              <div>
                <div className="prediction-hero-title">Prediction {idx + 1}: {pred.title}</div>
                {pred.subtitle && <div className="prediction-hero-subtitle">{pred.subtitle}</div>}
              </div>
            </div>
          </div>

          <h2 className="section-title break-before">The Dystopian Story</h2>
          <div className="dystopian-section">
            {pred.dystopian.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {pred.mechanismSummary && pred.mechanismSummary.length > 0 && (
            <>
              <h3 className="kicker">Mechanism Summary</h3>
              <div className="meta-block">
                {pred.mechanismSummary.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </>
          )}

          {pred.callout && (
            <CalloutBox color={pred.callout.color} title={pred.callout.title}>
              {pred.callout.content}
            </CalloutBox>
          )}

          {(() => {
            const explicitCharts = Array.isArray(pred.charts) ? pred.charts.filter(Boolean) : [];
            const chartsToRender = explicitCharts.length > 0 ? explicitCharts : getFallbackPredictionCharts(idx);
            if (!chartsToRender || chartsToRender.length === 0) return null;

            return chartsToRender.map((chart, chartIdx) => {
              // Try to get chart data from JSON first, then fall back to reportCharts.js
              const chartData = reportData.charts?.[chart.id] || reportCharts[chart.id];
            if (!chartData) return null;

            return (
              <div key={chartIdx}>
                <div className="chart-container avoid-break">
                  <div className="chart-title">{chart.title || chartData.title || ''}</div>
                  <div className="chart-subtitle">{chart.subtitle || chartData.subtitle || ''}</div>
                  <ResponsiveContainer width="100%" height={220}>
                    {renderChartById(chart.id, chartData) || renderGenericChart(chart.id, chartData) || <div />}
                  </ResponsiveContainer>
                  {chart.explanation && (
                    <p style={{ fontSize: '8pt', color: COLORS.grey, marginTop: '1em', textAlign: 'center' }}>
                      <strong>Interpretation:</strong> {chart.explanation}
                    </p>
                  )}

                  {chart.narrative && (
                    <div style={{ marginTop: '10px' }}>
                      {chart.narrative.whatItShows && renderRichLine(`**What it shows:** ${chart.narrative.whatItShows}`)}
                      {chart.narrative.whyItMatters && renderRichLine(`**Why it matters:** ${chart.narrative.whyItMatters}`)}
                      {chart.narrative.whatChangesIt && renderRichLine(`**What changes it:** ${chart.narrative.whatChangesIt}`)}
                    </div>
                  )}
                </div>
              </div>
            );
            });
          })()}

          {pred.analysis && pred.analysis.length > 0 && (
            <div style={{ marginTop: '1em' }}>
              {pred.analysis.map((p, i) => (
                <React.Fragment key={i}>{renderRichLine(p)}</React.Fragment>
              ))}
            </div>
          )}

          {pred.whyThisHappens && pred.whyThisHappens.length > 0 && (
            <>
              <h2 className="section-title">Why This Happens</h2>
              <div className="mitigated-section">
                {pred.whyThisHappens.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </>
          )}

          {pred.logic && pred.logic.length > 0 && (
            <>
              <h3 className="kicker">Logic</h3>
              <LogicBox logic={pred.logic} nodeLabels={reportData.nodeLabels} />
            </>
          )}

          {pred.countermeasures && (
            <>
              <h2 className="section-title break-before">Countermeasures</h2>
              
              {/* Intervention Window */}
              {(pred.countermeasures.status || pred.countermeasures.window || pred.countermeasures.node) && (
                <div style={{
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '14px 16px',
                  marginBottom: '16px',
                  fontSize: '8.5pt'
                }}>
                  {pred.countermeasures.status && (
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: '600', color: '#495057' }}>Status: </span>
                      <span style={{ color: '#6c757d' }}>{pred.countermeasures.status}</span>
                    </div>
                  )}
                  {pred.countermeasures.window && (
                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: '600', color: '#495057' }}>Window: </span>
                      <span style={{ color: '#6c757d' }}>{pred.countermeasures.window}</span>
                    </div>
                  )}
                  {pred.countermeasures.node && (
                    <div>
                      <span style={{ fontWeight: '600', color: '#495057' }}>Node: </span>
                      <span style={{ color: '#6c757d' }}>
                        {pred.countermeasures.node}
                        {reportData.nodeLabels?.[pred.countermeasures.node] ? ` — ${reportData.nodeLabels[pred.countermeasures.node]}` : ''}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Strategic Actions */}
              {pred.countermeasures.actions && pred.countermeasures.actions.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: pred.countermeasures.actions.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  {pred.countermeasures.actions.map((a) => (
                    <ActionBlock
                      key={a.title}
                      title={a.title}
                      agent={a.agent}
                      brief={a.brief}
                      mechanism={a.mechanism}
                      impact={a.impact}
                      odds={a.odds}
                      friction={a.friction}
                      rationale={a.rationale}
                      failureMode={a.failureMode}
                    />
                  ))}
                </div>
              )}

              {/* Combined Probability & Outcomes */}
              {pred.countermeasures.combinedProbability && (
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '16px',
                  fontSize: '8.5pt'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{ fontWeight: '600', color: '#495057', fontSize: '9pt' }}>Combined Probability: </span>
                    <span style={{ color: '#212529', fontWeight: '600' }}><MarkdownField content={pred.countermeasures.combinedProbability} /></span>
                  </div>
                  
                  {pred.countermeasures.combinedExplanation && !combinedExplanationLooksLikeActionDetails(pred.countermeasures.combinedExplanation) && (
                    <div style={{ marginBottom: '12px', color: '#6c757d', lineHeight: '1.5' }}>
                      <MarkdownField content={pred.countermeasures.combinedExplanation} />
                    </div>
                  )}
                  
                  {(pred.countermeasures.outcomes?.ifWorks || pred.countermeasures.outcomes?.ifFails) && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: pred.countermeasures.outcomes?.ifWorks && pred.countermeasures.outcomes?.ifFails ? '1fr 1fr' : '1fr',
                      gap: '12px',
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #e9ecef'
                    }}>
                      {pred.countermeasures.outcomes?.ifWorks && (
                        <div>
                          <div style={{ fontWeight: '600', color: '#28a745', marginBottom: '4px', fontSize: '8pt', textTransform: 'uppercase', letterSpacing: '0.5px' }}>If It Works</div>
                          <div style={{ color: '#6c757d', fontSize: '8pt' }}>{pred.countermeasures.outcomes.ifWorks}</div>
                        </div>
                      )}
                      {pred.countermeasures.outcomes?.ifFails && (
                        <div>
                          <div style={{ fontWeight: '600', color: '#dc3545', marginBottom: '4px', fontSize: '8pt', textTransform: 'uppercase', letterSpacing: '0.5px' }}>If It Fails</div>
                          <div style={{ color: '#6c757d', fontSize: '8pt' }}>{pred.countermeasures.outcomes.ifFails}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <h2 className="section-title" style={{ marginTop: '2.5em' }}>The Mitigated Story</h2>
          <div className="mitigated-section">
            {pred.mitigated.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <h2 className="section-title">Signal Validation</h2>
          <div className="signal-validation">
            {pred.signal.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {pred.signalStrength && (
              <p style={{ marginTop: '8px', fontWeight: '700' }}>
                Signal Strength: {pred.signalStrength}
              </p>
            )}
          </div>

          {pred.interventionWindow && pred.interventionWindow.length > 0 && (
            <>
              <h3 className="kicker">Intervention Window</h3>
              <div className="dystopian-section">
                {pred.interventionWindow.map((p, i) => (
                  <p key={i}><MarkdownField content={p} /></p>
                ))}
              </div>
            </>
          )}

          {idx === (reportData?.narrative?.predictions?.length || 0) - 1 && (
            <div
              className="avoid-break"
              style={{
                marginTop: '12px',
                background: '#000000',
                border: `2px solid ${COLORS.yellow}`,
                borderRadius: '10px',
                padding: '14px'
              }}
            >
              <div
                style={{
                  color: COLORS.yellow,
                  fontWeight: 900,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  fontSize: '10pt'
                }}
              >
                Get in touch
              </div>
              <div style={{ color: '#ffffff', fontSize: '10pt', marginTop: '8px', lineHeight: '1.5' }}>
                If you want to apply this chapter to your organisation—intervention windows, prioritisation, and implementation planning—contact us.
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                <a
                  href={`mailto:hello@obsolete.com?subject=${encodeURIComponent('2029 Predictions Report: Next steps')}`}
                  style={{
                    textDecoration: 'none',
                    background: 'transparent',
                    color: COLORS.yellow,
                    border: `2px solid ${COLORS.yellow}`,
                    padding: '10px 12px',
                    borderRadius: '10px',
                    fontWeight: 900,
                    fontSize: '9.5pt',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em'
                  }}
                >
                  Email hello@obsolete.com
                </a>
                {reportData?.nextSteps?.ctaHref && (
                  <a
                    href={CONTACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: 'none',
                      background: COLORS.yellow,
                      color: '#111111',
                      border: `2px solid ${COLORS.yellow}`,
                      padding: '10px 12px',
                      borderRadius: '10px',
                      fontWeight: 900,
                      fontSize: '9.5pt',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em'
                    }}
                  >
                    {reportData.nextSteps.ctaLabel || 'Book consultation'}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* CTA PAGE */}
      {reportData.images.cta && (
        <div className="content-page break-before">
          <ReportImage
            src={reportData.images.cta}
            alt="CTA"
            className="report-image"
            style={{ width: '100%', height: 'auto' }}
          />
        </div>
      )}

      {reportData.decisionFramework && (
        <div className="content-page break-before">
          <h1>{reportData.decisionFramework.title}</h1>
          <p>{reportData.decisionFramework.intro}</p>

          <h2>{reportData.decisionFramework.whatToMeasureTitle}</h2>
          <div className="signal-validation">
            {reportData.decisionFramework.whatToMeasure.map((p, i) => (
              <p key={i}><strong>{p}</strong></p>
            ))}
          </div>

          <h2>{reportData.decisionFramework.whatToDoTitle}</h2>
          <CalloutBox color={reportData.decisionFramework.priorityActions.color} title={reportData.decisionFramework.priorityActions.title}>
            {reportData.decisionFramework.priorityActions.content}
          </CalloutBox>
        </div>
      )}

      {reportData.nextSteps && (
        <div className="content-page break-before">
          <h1>{reportData.nextSteps.title}</h1>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '14px', alignItems: 'start' }}>
            <div style={{ border: '1px solid #dfe6e9', borderRadius: '14px', background: '#ffffff', padding: '14px 14px' }}>
              <h2 style={{ marginTop: 0 }}>{reportData.nextSteps.cardTitle}</h2>
              <p>{reportData.nextSteps.cardBody}</p>
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  marginTop: '10px',
                  background: COLORS.yellow,
                  color: '#111',
                  fontWeight: 900,
                  fontSize: '10pt',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  textDecoration: 'none'
                }}
              >
                {reportData.nextSteps.ctaLabel}
              </a>
              <div style={{ color: '#636e72', fontSize: '9.5pt', marginTop: '6px' }}>{reportData.nextSteps.ctaMuted}</div>
            </div>
            <div>
              <ReportImage
                src={reportData.images.cta}
                alt="Live CTA"
                className="prediction-hero-image"
                style={{ width: '100%', height: '320px', borderRadius: '14px', objectFit: 'cover', border: '1px solid #dfe6e9' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformingReportTemplate;
